/**
 * Server Profile Data Entry Point
 *
 * Exports all server profiles and lookup functions.
 */

import type { ServerProfile, ServerRole, CoolingType } from '../../types/servers';

// Dell Servers
export * from './dell-xe9680';
export * from './dell-xe8640';
export * from './dell-r760xa';

// Supermicro Servers
export * from './supermicro-sys821ge';
export * from './supermicro-sys521ge';
export * from './supermicro-sys420gp';

// Import individual server profiles
import { DELL_XE9680_H100, DELL_XE9680_B200 } from './dell-xe9680';
import { DELL_XE8640_H100 } from './dell-xe8640';
import { DELL_R760XA_RTX6000, DELL_R760XA_L40S } from './dell-r760xa';
import { SUPERMICRO_SYS821GE_H100, SUPERMICRO_SYS821GE_H200 } from './supermicro-sys821ge';
import {
  SUPERMICRO_SYS521GE_L40S_8GPU,
  SUPERMICRO_SYS521GE_L40S_10GPU,
  SUPERMICRO_SYS521GE_RTX6000,
} from './supermicro-sys521ge';
import { SUPERMICRO_SYS420GP_A100 } from './supermicro-sys420gp';

/**
 * All available server profiles
 */
export const ALL_SERVERS: readonly ServerProfile[] = [
  // Dell Servers
  DELL_XE9680_H100,
  DELL_XE9680_B200,
  DELL_XE8640_H100,
  DELL_R760XA_RTX6000,
  DELL_R760XA_L40S,

  // Supermicro Servers
  SUPERMICRO_SYS821GE_H100,
  SUPERMICRO_SYS821GE_H200,
  SUPERMICRO_SYS521GE_L40S_8GPU,
  SUPERMICRO_SYS521GE_L40S_10GPU,
  SUPERMICRO_SYS521GE_RTX6000,
  SUPERMICRO_SYS420GP_A100,
] as const;

/**
 * Server lookup map for fast ID-based access
 */
export const SERVER_MAP = new Map<string, ServerProfile>(
  ALL_SERVERS.map((server) => [server.id, server])
);

/**
 * Gets a server by ID
 * @param id - Server ID
 * @returns Server profile or undefined if not found
 */
export function getServerById(id: string): ServerProfile | undefined {
  return SERVER_MAP.get(id);
}

/**
 * Gets servers by vendor
 * @param vendor - Vendor name (Dell, Supermicro, etc.)
 * @returns Array of servers from that vendor
 */
export function getServersByVendor(vendor: string): readonly ServerProfile[] {
  return ALL_SERVERS.filter(
    (server) => server.vendor.toLowerCase() === vendor.toLowerCase()
  );
}

/**
 * Gets servers by platform ID
 * @param platformId - GPU platform ID
 * @returns Array of compatible servers
 */
export function getServersByPlatform(platformId: string): readonly ServerProfile[] {
  return ALL_SERVERS.filter((server) => server.gpu?.platformId === platformId);
}

/**
 * Gets servers by rack units
 * @param rackUnits - Number of rack units (2U, 4U, 5U, 6U, 8U)
 * @returns Array of servers with that form factor
 */
export function getServersByRackUnits(rackUnits: number): readonly ServerProfile[] {
  return ALL_SERVERS.filter((server) => server.dimensions.rackUnits === rackUnits);
}

/**
 * Gets servers by cooling type
 * @param coolingType - Cooling type (air, liquid, hybrid)
 * @returns Array of servers with that cooling type
 */
export function getServersByCooling(coolingType: CoolingType): readonly ServerProfile[] {
  return ALL_SERVERS.filter((server) => server.environmental.coolingType === coolingType);
}

/**
 * Gets servers by GPU count
 * @param gpuCount - Number of GPUs per server
 * @returns Array of servers with that GPU count
 */
export function getServersByGpuCount(gpuCount: number): readonly ServerProfile[] {
  return ALL_SERVERS.filter((server) => server.gpu?.gpusPerNode === gpuCount);
}

/**
 * Gets servers suitable for HGX platforms
 * @returns Array of HGX-capable servers
 */
export function getHGXServers(): readonly ServerProfile[] {
  return ALL_SERVERS.filter(
    (server) =>
      server.gpu?.platformId.startsWith('hgx-') ||
      server.dimensions.rackUnits >= 4 // HGX typically requires 4U+
  );
}

/**
 * Gets servers suitable for PCIe GPUs
 * @returns Array of PCIe GPU servers
 */
export function getPCIeServers(): readonly ServerProfile[] {
  return ALL_SERVERS.filter(
    (server) =>
      server.gpu?.platformId === 'l40s' ||
      server.gpu?.platformId === 'rtx-6000-ada' ||
      server.dimensions.rackUnits <= 5
  );
}

/**
 * Gets air-cooled servers
 * @returns Array of air-cooled servers
 */
export function getAirCooledServers(): readonly ServerProfile[] {
  return ALL_SERVERS.filter((server) => server.environmental.coolingType === 'air');
}

/**
 * Gets liquid-cooled servers
 * @returns Array of liquid-cooled servers
 */
export function getLiquidCooledServers(): readonly ServerProfile[] {
  return ALL_SERVERS.filter((server) => server.environmental.coolingType === 'liquid');
}

/**
 * Gets Dell servers only
 * @returns Array of Dell servers
 */
export function getDellServers(): readonly ServerProfile[] {
  return getServersByVendor('Dell');
}

/**
 * Gets Supermicro servers only
 * @returns Array of Supermicro servers
 */
export function getSupermicroServers(): readonly ServerProfile[] {
  return getServersByVendor('Supermicro');
}

/**
 * Filters servers by maximum power consumption
 * @param maxPowerWatts - Maximum power in watts
 * @returns Array of servers within power budget
 */
export function getServersByPowerBudget(maxPowerWatts: number): readonly ServerProfile[] {
  return ALL_SERVERS.filter((server) => server.power.typicalPowerWatts <= maxPowerWatts);
}

/**
 * Filters servers by role
 * @param role - Server role (compute, management, storage, gateway)
 * @returns Array of servers with that role
 */
export function getServersByRole(role: ServerRole): readonly ServerProfile[] {
  return ALL_SERVERS.filter((server) => server.role === role);
}

/**
 * Gets compute servers only
 * @returns Array of compute servers
 */
export function getComputeServers(): readonly ServerProfile[] {
  return getServersByRole('compute');
}

/**
 * Calculates servers needed for target GPU count
 * @param targetGpuCount - Target number of GPUs
 * @param gpusPerServer - GPUs per server
 * @returns Number of servers needed
 */
export function calculateServerCount(targetGpuCount: number, gpusPerServer: number): number {
  return Math.ceil(targetGpuCount / gpusPerServer);
}

/**
 * Calculates rack units needed for servers
 * @param serverCount - Number of servers
 * @param rackUnitsPerServer - Rack units per server
 * @returns Total rack units needed
 */
export function calculateRackUnits(serverCount: number, rackUnitsPerServer: number): number {
  return serverCount * rackUnitsPerServer;
}

/**
 * Calculates servers per rack
 * @param rackUnits - Available rack units (typically 42U, 48U, or 52U)
 * @param rackUnitsPerServer - Rack units per server
 * @returns Number of servers that fit in rack
 */
export function calculateServersPerRack(rackUnits: number, rackUnitsPerServer: number): number {
  return Math.floor(rackUnits / rackUnitsPerServer);
}

/**
 * Validates that a server ID exists
 * @param id - Server ID to validate
 * @returns True if server exists
 */
export function isServerIdValid(id: string): boolean {
  return SERVER_MAP.has(id);
}

/**
 * Gets server IDs as an array
 * @returns Array of all server IDs
 */
export function getAllServerIds(): readonly string[] {
  return ALL_SERVERS.map((server) => server.id);
}

/**
 * Gets server model names as array
 * @returns Array of all server models
 */
export function getAllServerModels(): readonly string[] {
  return ALL_SERVERS.map((server) => server.model);
}
